import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { FaEnvelope, FaLock } from 'react-icons/fa';
import { MdPhoneAndroid } from 'react-icons/md';
import { authAPI } from '../services/api';
import PlayerRegistrationConsentSection from '../components/playerSignup/PlayerRegistrationConsentSection';
import SkillReferenceModal from '../components/playerSignup/SkillReferenceModal';
import {
  exactLevelsForRange,
  cantonOptionLabel,
  PLAYTOMIC_BAND_OPTIONS,
  SWISS_PADEL_LEVEL_OPTIONS,
  deriveSkillRangeFromSelections,
  skillRangeHpsLabel,
} from '../constants/playerSignup';

const BRAND = { blue: '#043663', gray: '#E5E7EB', lightGray: '#f3f4f6' };

const PageWrap = styled.div`
  max-width: 440px;
  margin: 2rem auto;
  padding: 0 1rem;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid ${BRAND.gray};
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.5rem 0;
`;

const Subtitle = styled.p`
  font-size: 0.9375rem;
  color: #6b7280;
  margin: 0 0 1.25rem 0;
`;

const StepTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.35rem 0;
`;

const StepLead = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 1rem;
  line-height: 1.5;
`;

const SectionHeading = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.35rem 0;
`;

const SectionLead = styled.p`
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0 0 1rem;
  line-height: 1.5;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.375rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.625rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9375rem;
  margin-bottom: 1rem;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: ${BRAND.blue};
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.12);
  }
`;

const InputAdornWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0 0.65rem;
  margin-bottom: 1rem;
  &:focus-within {
    border-color: ${BRAND.blue};
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.12);
  }
  svg {
    flex-shrink: 0;
    color: #9ca3af;
  }
  input {
    flex: 1;
    border: none;
    padding: 0.625rem 0.25rem;
    font-size: 0.9375rem;
    min-width: 0;
    background: transparent;
    &:focus {
      outline: none;
    }
  }
`;

const TogglePw = styled.button`
  flex-shrink: 0;
  border: none;
  background: none;
  color: ${BRAND.blue};
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.35rem;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.625rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9375rem;
  margin-bottom: 1rem;
  background: #fff;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: ${BRAND.blue};
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.12);
  }
`;

const BtnPrimary = styled.button`
  flex: 1;
  padding: 0.75rem 1.25rem;
  background: ${BRAND.blue};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 0.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  &:hover:not(:disabled) {
    background: #032a4a;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const BtnOutline = styled.button`
  flex: 1;
  padding: 0.75rem 1rem;
  background: transparent;
  color: ${BRAND.blue};
  border: 2px solid ${BRAND.blue};
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 0.5rem;
  &:hover:not(:disabled) {
    background: rgba(4, 54, 99, 0.06);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const BtnRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const FreeBadge = styled.span`
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #059669;
  background: #d1fae5;
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  margin-left: 0.5rem;
`;

const LoginLink = styled.div`
  text-align: center;
  margin-top: 1.5rem;
  font-size: 0.9375rem;
  color: #6b7280;
  a {
    color: ${BRAND.blue};
    font-weight: 500;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Hint = styled.p`
  font-size: 0.8125rem;
  color: #6b7280;
  margin: -0.25rem 0 1rem 0;
`;

const TurnstileBox = styled.div`
  margin: 1rem 0;
  min-height: 70px;
  display: flex;
  justify-content: center;
`;

const BtnSecondary = styled.button`
  width: 100%;
  padding: 0.625rem 1rem;
  background: #fff;
  color: ${BRAND.blue};
  border: 1px solid ${BRAND.blue};
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 0.75rem;
  &:hover:not(:disabled) {
    background: ${BRAND.lightGray};
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const StepperRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1.25rem;
  padding: 0 0.1rem;
  position: relative;
`;

const StepperTrack = styled.div`
  position: absolute;
  top: 14px;
  left: 12%;
  right: 12%;
  height: 2px;
  background: ${BRAND.gray};
  border-radius: 999px;
  z-index: 0;
`;

const StepperFill = styled(motion.div)`
  position: absolute;
  top: 14px;
  left: 12%;
  height: 2px;
  border-radius: 999px;
  background: linear-gradient(90deg, ${BRAND.blue}, #0a558c);
  z-index: 0;
`;

const StepItem = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  min-width: 0;
`;

const StepBubble = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  background: ${(p) => (p.$done || p.$active ? BRAND.blue : '#e5e7eb')};
  color: ${(p) => (p.$done || p.$active ? '#fff' : '#9ca3af')};
`;

const StepLabel = styled.span`
  font-size: 0.55rem;
  font-weight: 600;
  color: ${(p) => (p.$active || p.$done ? BRAND.blue : '#9ca3af')};
  margin-top: 0.25rem;
  text-align: center;
  line-height: 1.1;
  max-width: 64px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const SummaryBlock = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.85rem 1rem;
  margin-bottom: 0.75rem;
`;

const SummaryHeading = styled.div`
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${BRAND.blue};
  margin-bottom: 0.25rem;
`;

const SummaryHeadRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.25rem;
`;

const SummaryBody = styled.p`
  font-size: 0.8125rem;
  color: #334155;
  margin: 0;
  line-height: 1.45;
`;

const SummaryEdit = styled.button`
  border: none;
  background: none;
  color: ${BRAND.blue};
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
`;

const PrivacyNote = styled.p`
  font-size: 0.75rem;
  color: #94a3b8;
  text-align: center;
  margin: 0.75rem 0 0;
`;

const SkillDerivedBox = styled.div`
  background: linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #7dd3fc;
  border-radius: 10px;
  padding: 0.85rem 1rem;
  margin-bottom: 1rem;
  font-size: 0.8125rem;
  color: #0c4a6e;
  line-height: 1.45;
`;

const SkillDerivedStrong = styled.strong`
  display: block;
  margin-bottom: 0.25rem;
  color: ${BRAND.blue};
`;

const BtnSkillInfo = styled.button`
  width: 100%;
  padding: 0.65rem 1rem;
  margin-bottom: 1rem;
  background: #f8fafc;
  color: ${BRAND.blue};
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: #f1f5f9;
    border-color: ${BRAND.blue};
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
`;

const FieldWrap = styled.div`
  min-width: 0;
`;

const WizardPanel = styled.div`
  min-height: 120px;
`;

const slideSx = {
  initial: { opacity: 0, x: 14 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -12 },
  transition: { duration: 0.22, ease: [0.4, 0, 0.2, 1] },
};

function WizardStepper({ wizardStep, totalSteps, labels }) {
  const fillPct =
    totalSteps <= 1 ? 0 : ((Math.min(wizardStep, totalSteps) - 1) / (totalSteps - 1)) * 100;
  return (
    <StepperRow>
      <StepperTrack />
      <StepperFill
        initial={{ width: '0%' }}
        animate={{ width: `${fillPct}%` }}
        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
      />
      {Array.from({ length: totalSteps }, (_, i) => {
        const step = i + 1;
        return (
          <StepItem key={step}>
            <StepBubble $active={wizardStep === step} $done={wizardStep > step}>
              {wizardStep > step ? '✓' : step}
            </StepBubble>
            <StepLabel $active={wizardStep === step} $done={wizardStep > step}>
              {labels[i] || step}
            </StepLabel>
          </StepItem>
        );
      })}
    </StepperRow>
  );
}

export default function CreatePlayerAccountPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    tenantId: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    dateOfBirth: '',
    sex: '',
    homeClub: '',
    homeCanton: '',
  });
  const [playtomicBandId, setPlaytomicBandId] = useState('');
  const [swissLevel, setSwissLevel] = useState('');
  const [skillMappingModalOpen, setSkillMappingModalOpen] = useState(false);
  const [exactSkillLevel, setExactSkillLevel] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeRules, setAgreeRules] = useState(false);
  const [partnerCommunications, setPartnerCommunications] = useState(false);

  const [signupStep, setSignupStep] = useState('form');
  const [pendingRegistrationId, setPendingRegistrationId] = useState(null);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [turnstileSiteKey, setTurnstileSiteKey] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileContainerRef = useRef(null);
  const turnstileWidgetIdRef = useRef(null);

  const wizardLabels = useMemo(
    () => [
      t('playerRegister.wizardStepAccount'),
      t('playerRegister.wizardStepDetails'),
      t('playerRegister.wizardStepClubSkill'),
      t('playerRegister.wizardStepReview'),
    ],
    [t]
  );

  const { data: signupConfigRes } = useQuery(['playerSignupConfig'], () => authAPI.getPlayerSignupConfig(), {
    staleTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    const k = signupConfigRes?.data?.turnstileSiteKey;
    setTurnstileSiteKey(typeof k === 'string' && k.trim() ? k.trim() : null);
  }, [signupConfigRes]);

  const { data: countriesRes, isLoading: loadingCountries } = useQuery(
    ['tenantsForPlayerSignup'],
    () => authAPI.getTenantsForPlayerSignup(),
    { staleTime: 5 * 60 * 1000 }
  );
  const { data: cantonOptsRes, isLoading: loadingCantons } = useQuery(
    ['playerSignupCantonOptions'],
    () => authAPI.getPlayerSignupCantonOptions(),
    { staleTime: 60 * 60 * 1000 }
  );
  const countries = useMemo(() => {
    const raw = countriesRes?.data;
    return Array.isArray(raw) ? raw : [];
  }, [countriesRes]);
  const cantonOptions = useMemo(() => {
    const raw = cantonOptsRes?.data;
    return Array.isArray(raw) ? raw : [];
  }, [cantonOptsRes]);

  useEffect(() => {
    if (countries.length === 0) return;
    const ch = countries.find((c) => String(c.countryCode || '').toUpperCase() === 'CH');
    const pick = ch || countries[0];
    const id = String(pick.id);
    setForm((prev) => (prev.tenantId === id ? prev : { ...prev, tenantId: id }));
  }, [countries]);

  const { skillRange } = useMemo(
    () => deriveSkillRangeFromSelections(playtomicBandId, swissLevel),
    [playtomicBandId, swissLevel]
  );

  useEffect(() => {
    setExactSkillLevel('');
  }, [skillRange]);

  const exactSkillOptions = useMemo(() => exactLevelsForRange(skillRange), [skillRange]);

  const signupCampaignsOnlyNotice = useMemo(
    () =>
      !!countries.find((c) => String(c.id) === String(form.tenantId))?.campaigns_only_for_new_registrations,
    [countries, form.tenantId]
  );

  const cantonLabel = useMemo(() => {
    const c = cantonOptions.find((x) => String(x.code) === String(form.homeCanton));
    if (!c) return form.homeCanton || '—';
    return cantonOptionLabel(c);
  }, [cantonOptions, form.homeCanton]);

  const skillSummary = useMemo(() => {
    if (!exactSkillLevel || !skillRange) return '—';
    const bandLabel = skillRangeHpsLabel(skillRange);
    const parts = [];
    if (playtomicBandId) {
      const row = PLAYTOMIC_BAND_OPTIONS.find((o) => o.id === playtomicBandId);
      if (row) parts.push(t(row.labelKey));
    }
    if (swissLevel && String(swissLevel).trim()) {
      const n = Number(swissLevel);
      const sRow = SWISS_PADEL_LEVEL_OPTIONS.find((o) => o.level === n);
      if (sRow) parts.push(t(sRow.labelKey));
    }
    const refLine = parts.length ? parts.join(' · ') : '';
    return refLine
      ? `${exactSkillLevel} (${bandLabel}) — ${refLine}`
      : `${exactSkillLevel} (${bandLabel})`;
  }, [skillRange, exactSkillLevel, playtomicBandId, swissLevel, t]);

  const step2Blocked = submitting || loadingCountries || countries.length === 0 || !form.tenantId;
  const step34Blocked = step2Blocked || loadingCantons || cantonOptions.length === 0;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetTurnstileWidget = () => {
    if (typeof window !== 'undefined' && window.turnstile && turnstileWidgetIdRef.current != null) {
      try {
        window.turnstile.reset(turnstileWidgetIdRef.current);
      } catch (_) {
        /* noop */
      }
    }
    setTurnstileToken('');
  };

  useEffect(() => {
    if (signupStep !== 'verify') return undefined;
    if (!turnstileSiteKey || !turnstileContainerRef.current) return undefined;

    const container = turnstileContainerRef.current;

    const renderInner = () => {
      if (typeof window === 'undefined' || !window.turnstile || !container) return;
      if (turnstileWidgetIdRef.current != null) {
        try {
          window.turnstile.remove(turnstileWidgetIdRef.current);
        } catch (_) {
          /* noop */
        }
        turnstileWidgetIdRef.current = null;
      }
      turnstileWidgetIdRef.current = window.turnstile.render(container, {
        sitekey: turnstileSiteKey,
        callback: (token) => setTurnstileToken(token || ''),
        'expired-callback': () => setTurnstileToken(''),
        'error-callback': () => setTurnstileToken(''),
      });
    };

    let scriptEl = document.querySelector('script[data-turnstile-loader="hps-create-player"]');
    if (!scriptEl) {
      scriptEl = document.createElement('script');
      scriptEl.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      scriptEl.async = true;
      scriptEl.defer = true;
      scriptEl.dataset.turnstileLoader = 'hps-create-player';
      scriptEl.onload = () => renderInner();
      document.body.appendChild(scriptEl);
    } else if (typeof window !== 'undefined' && window.turnstile) {
      renderInner();
    }

    const onLoad = () => renderInner();
    if (scriptEl && typeof window !== 'undefined' && !window.turnstile) {
      scriptEl.addEventListener('load', onLoad);
    }

    return () => {
      if (scriptEl) scriptEl.removeEventListener('load', onLoad);
      if (typeof window !== 'undefined' && window.turnstile && turnstileWidgetIdRef.current != null) {
        try {
          window.turnstile.remove(turnstileWidgetIdRef.current);
        } catch (_) {
          /* noop */
        }
        turnstileWidgetIdRef.current = null;
      }
    };
  }, [signupStep, turnstileSiteKey]);

  const validateStep1 = () => {
    if (!form.email?.trim()) {
      toast.error(t('playerRegister.errorEmail'));
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error(t('playerRegister.errorEmailInvalid'));
      return false;
    }
    if ((form.password || '').length < 6) {
      toast.error(t('playerRegister.errorPasswordMin'));
      return false;
    }
    if (form.password !== form.confirmPassword) {
      toast.error(t('playerRegister.errorPasswordMatch'));
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!form.tenantId) {
      toast.error(t('playerRegister.errorRegion'));
      return false;
    }
    if (!form.firstName?.trim()) {
      toast.error(t('playerRegister.errorFirstName'));
      return false;
    }
    if (!form.lastName?.trim()) {
      toast.error(t('playerRegister.errorLastName'));
      return false;
    }
    if (!form.phoneNumber?.trim()) {
      toast.error(t('playerRegister.errorPhone'));
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!form.homeCanton || !String(form.homeCanton).trim()) {
      toast.error(t('playerRegister.errorCanton'));
      return false;
    }
    const hasPlaytomic = !!(playtomicBandId && String(playtomicBandId).trim());
    const hasSwiss = !!(swissLevel && String(swissLevel).trim());
    if (!hasPlaytomic && !hasSwiss) {
      toast.error(t('playerRegister.errorSkillReferenceRequired'));
      return false;
    }
    if (!skillRange) {
      toast.error(t('playerRegister.errorSkillBand'));
      return false;
    }
    if (!exactSkillLevel) {
      toast.error(t('playerRegister.errorExactSkill'));
      return false;
    }
    return true;
  };

  const goNext = () => {
    if (wizardStep === 1) {
      if (!validateStep1()) return;
      setWizardStep(2);
      return;
    }
    if (wizardStep === 2) {
      if (!validateStep2()) return;
      setWizardStep(3);
      return;
    }
    if (wizardStep === 3) {
      if (!validateStep3()) return;
      setWizardStep(4);
    }
  };

  const goBack = () => setWizardStep((s) => Math.max(1, s - 1));

  const runRegisterStart = async () => {
    if (!agreeTerms) {
      toast.error(t('register.agreeTermsRequired'));
      return;
    }
    if (!agreeRules) {
      toast.error(t('register.agreeRulesRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await authAPI.registerPlayerStart({
        tenantId: form.tenantId,
        email: form.email.trim(),
        password: form.password,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phoneNumber: form.phoneNumber.trim(),
        dateOfBirth: '',
        sex: '',
        homeClub: '',
        homeCanton: form.homeCanton.trim(),
        skillLevel: parseFloat(exactSkillLevel),
        instagramHandle: null,
        agreeTerms: true,
        agreeRules: true,
        partnerCommunications: !!partnerCommunications,
      });
      if (res?.success && res?.data?.pendingRegistrationId) {
        setPendingRegistrationId(res.data.pendingRegistrationId);
        setMaskedEmail(res.data.maskedEmail || '');
        setSignupStep('verify');
        setVerificationCode('');
        setTurnstileToken('');
        toast.success(res.message || 'Check your email for a verification code.');
      } else {
        toast.error(res?.message || 'Could not send verification email');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Registration failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!pendingRegistrationId) return;
    setSubmitting(true);
    try {
      const res = await authAPI.registerPlayerResendCode({ pendingRegistrationId });
      if (res?.success) {
        toast.success(res.message || 'New code sent.');
        if (res?.data?.maskedEmail) setMaskedEmail(res.data.maskedEmail);
        resetTurnstileWidget();
      } else {
        toast.error(res?.message || 'Resend failed');
      }
    } catch (err) {
      const m = err?.response?.data?.message || err?.message || 'Resend failed';
      toast.error(m);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyComplete = async (e) => {
    e.preventDefault();
    const codeNorm = verificationCode.trim().replace(/\s/g, '');
    if (!/^\d{6}$/.test(codeNorm)) {
      toast.error('Enter the 6-digit code from your email.');
      return;
    }
    if (turnstileSiteKey && !turnstileToken) {
      toast.error('Complete the verification checkbox below.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await authAPI.registerPlayerComplete({
        pendingRegistrationId,
        code: codeNorm,
        turnstileToken: turnstileSiteKey ? turnstileToken : '',
        inviteToken: searchParams.get('invite') || undefined,
      });
      if (res?.success) {
        toast.success(res.message || 'Account created. You can now log in.');
        navigate('/login?registered=1');
      } else {
        toast.error(res?.message || 'Verification failed');
        resetTurnstileWidget();
      }
    } catch (err) {
      const m = err?.response?.data?.message || err?.message || 'Verification failed';
      toast.error(m);
      resetTurnstileWidget();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageWrap>
        <Card>
          <Title>
            {signupStep === 'verify' ? t('playerRegister.verifyTitle') : t('playerRegister.heroTitle')}
            {signupStep !== 'verify' ? <FreeBadge>Free</FreeBadge> : null}
          </Title>
          <Subtitle>
            {signupStep === 'verify' ? t('playerRegister.verifySubtitle') : t('playerRegister.heroSubtitle')}
          </Subtitle>

          {signupStep === 'verify' ? (
            <form onSubmit={handleVerifyComplete}>
              <Label>{t('playerRegister.verificationCodeLabel')}</Label>
              <p style={{ fontSize: '0.875rem', color: '#4b5563', marginTop: 0, marginBottom: '0.75rem' }}>
                {t('playerRegister.sentTo')}{' '}
                {maskedEmail ? <strong>{maskedEmail}</strong> : t('playerRegister.yourEmail')}
              </p>
              <Input
                name="verificationCode"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={verificationCode}
                onChange={(ev) => setVerificationCode(ev.target.value.replace(/[^\d\s]/g, ''))}
                placeholder={t('playerRegister.verificationCodePlaceholder')}
                maxLength={8}
              />
              {turnstileSiteKey ? (
                <>
                  <Label>{t('playerRegister.securityCheck')}</Label>
                  <TurnstileBox ref={turnstileContainerRef} />
                  <Hint>{t('playerRegister.turnstileHint')}</Hint>
                </>
              ) : (
                <Hint>{t('playerRegister.noCaptchaDev')}</Hint>
              )}
              <BtnPrimary
                type="submit"
                disabled={
                  submitting ||
                  !/^\d{6}$/.test(verificationCode.trim().replace(/\s/g, '')) ||
                  (!!turnstileSiteKey && !turnstileToken)
                }
              >
                {submitting ? t('playerRegister.creatingAccount') : t('playerRegister.createAccount')}
              </BtnPrimary>
              <BtnSecondary type="button" disabled={submitting || !pendingRegistrationId} onClick={handleResend}>
                {t('playerRegister.resendCode')}
              </BtnSecondary>
            </form>
          ) : (
            <>
              <WizardStepper wizardStep={wizardStep} totalSteps={4} labels={wizardLabels} />
              <WizardPanel>
                <AnimatePresence mode="wait">
                  {wizardStep === 1 && (
                    <motion.div key="w1" {...slideSx}>
                      <StepTitle>{t('playerRegister.step1Title')}</StepTitle>
                      <StepLead>{t('playerRegister.step1Lead')}</StepLead>
                      <Label htmlFor="cpa-email">{t('playerRegister.emailLabel')}</Label>
                      <InputAdornWrap>
                        <FaEnvelope aria-hidden />
                        <input
                          id="cpa-email"
                          name="email"
                          type="email"
                          value={form.email}
                          onChange={handleChange}
                          autoComplete="email"
                          placeholder={t('playerRegister.emailPlaceholder')}
                        />
                      </InputAdornWrap>
                      <Label htmlFor="cpa-pw">{t('playerRegister.passwordLabel')}</Label>
                      <InputAdornWrap>
                        <FaLock aria-hidden />
                        <input
                          id="cpa-pw"
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          value={form.password}
                          onChange={handleChange}
                          autoComplete="new-password"
                          placeholder={t('playerRegister.passwordPlaceholder')}
                        />
                        <TogglePw type="button" tabIndex={-1} onClick={() => setShowPassword((v) => !v)}>
                          {showPassword ? t('playerRegister.hidePassword') : t('playerRegister.showPassword')}
                        </TogglePw>
                      </InputAdornWrap>
                      <Label htmlFor="cpa-pw2">{t('playerRegister.confirmPasswordLabel')}</Label>
                      <InputAdornWrap>
                        <FaLock aria-hidden />
                        <input
                          id="cpa-pw2"
                          name="confirmPassword"
                          type={showPassword ? 'text' : 'password'}
                          value={form.confirmPassword}
                          onChange={handleChange}
                          autoComplete="new-password"
                          placeholder={t('playerRegister.confirmPasswordPlaceholder')}
                        />
                      </InputAdornWrap>
                      <BtnPrimary type="button" style={{ width: '100%' }} onClick={goNext}>
                        {t('playerRegister.continue')}
                      </BtnPrimary>
                    </motion.div>
                  )}

                  {wizardStep === 2 && (
                    <motion.div key="w2" {...slideSx}>
                      <StepTitle>{t('playerRegister.step2Title')}</StepTitle>
                      <StepLead>{t('playerRegister.step2Lead')}</StepLead>
                      <Row>
                        <FieldWrap>
                          <Label>{t('playerRegister.firstName')}</Label>
                          <Input
                            name="firstName"
                            value={form.firstName}
                            onChange={handleChange}
                            autoComplete="given-name"
                            placeholder={t('playerRegister.firstName')}
                          />
                        </FieldWrap>
                        <FieldWrap>
                          <Label>{t('playerRegister.lastName')}</Label>
                          <Input
                            name="lastName"
                            value={form.lastName}
                            onChange={handleChange}
                            autoComplete="family-name"
                            placeholder={t('playerRegister.lastName')}
                          />
                        </FieldWrap>
                      </Row>
                      <Label>{t('playerRegister.phoneLabel')}</Label>
                      <InputAdornWrap>
                        <MdPhoneAndroid aria-hidden />
                        <input
                          name="phoneNumber"
                          type="tel"
                          value={form.phoneNumber}
                          onChange={handleChange}
                          placeholder={t('playerRegister.phonePlaceholder')}
                        />
                      </InputAdornWrap>
                      {signupCampaignsOnlyNotice ? (
                        <Hint style={{ color: '#047857' }}>{t('playerRegister.campaignFirstNotice')}</Hint>
                      ) : null}
                      {loadingCountries ? <Hint style={{ color: '#0369a1' }}>{t('playerRegister.loadingRegions')}</Hint> : null}
                      <BtnRow>
                        <BtnOutline type="button" onClick={goBack}>
                          {t('playerRegister.back')}
                        </BtnOutline>
                        <BtnPrimary type="button" disabled={step2Blocked} onClick={goNext}>
                          {t('playerRegister.continue')}
                        </BtnPrimary>
                      </BtnRow>
                    </motion.div>
                  )}

                  {wizardStep === 3 && (
                    <motion.div key="w3" {...slideSx}>
                      <SectionHeading>{t('playerRegister.step3ClubSectionTitle')}</SectionHeading>
                      <SectionLead>{t('playerRegister.step3ClubSectionLead')}</SectionLead>
                      <Label>{t('playerRegister.homeCantonLabel')}</Label>
                      <Select
                        name="homeCanton"
                        value={form.homeCanton}
                        onChange={handleChange}
                        disabled={loadingCantons || cantonOptions.length === 0}
                      >
                        <option value="">{t('playerRegister.selectCanton')}</option>
                        {cantonOptions.map((c) => {
                          const code = c.code != null ? String(c.code) : '';
                          if (!code) return null;
                          return (
                            <option key={code} value={code}>
                              {cantonOptionLabel(c)}
                            </option>
                          );
                        })}
                      </Select>

                      <SectionHeading style={{ marginTop: '1.5rem' }}>
                        {t('playerRegister.step3SkillSectionTitle')}
                      </SectionHeading>
                      <Hint style={{ marginBottom: '0.75rem' }}>
                        {t('phase2.playerProfile.hints.skillOncePlaytomicSwiss')}
                      </Hint>
                      <Label>{t('playerRegister.playtomicBandLabel')}</Label>
                      <Select
                        value={playtomicBandId}
                        onChange={(e) => {
                          const v = e.target.value;
                          setPlaytomicBandId(v);
                          if (v) setSwissLevel('');
                        }}
                      >
                        <option value="">{t('playerRegister.playtomicBandPlaceholder')}</option>
                        {PLAYTOMIC_BAND_OPTIONS.map((o) => (
                          <option key={o.id} value={o.id}>
                            {t(o.labelKey)}
                          </option>
                        ))}
                      </Select>
                      <Label>{t('playerRegister.swissPadelLevelLabel')}</Label>
                      <Select
                        value={swissLevel}
                        onChange={(e) => {
                          const v = e.target.value;
                          setSwissLevel(v);
                          if (v) setPlaytomicBandId('');
                        }}
                      >
                        <option value="">{t('playerRegister.swissPadelLevelPlaceholder')}</option>
                        {SWISS_PADEL_LEVEL_OPTIONS.map((o) => (
                          <option key={o.level} value={String(o.level)}>
                            {t(o.labelKey)}
                          </option>
                        ))}
                      </Select>
                      <Hint>{t('playerRegister.skillReferenceHint')}</Hint>
                      <BtnSkillInfo type="button" onClick={() => setSkillMappingModalOpen(true)}>
                        {t('playerRegister.skillMappingMoreInfo')}
                      </BtnSkillInfo>
                      {skillRange ? (
                        <SkillDerivedBox>
                          <SkillDerivedStrong>{t('playerRegister.skillDerivedTitle')}</SkillDerivedStrong>
                          {t('playerRegister.skillLockNotice')} <strong>{skillRangeHpsLabel(skillRange)}</strong>
                        </SkillDerivedBox>
                      ) : null}
                      {skillRange ? (
                        <>
                          <Label>{t('playerRegister.exactHpsLevelLabel')}</Label>
                          <Select value={exactSkillLevel} onChange={(e) => setExactSkillLevel(e.target.value)}>
                            <option value="">{t('playerRegister.exactHpsLevelPlaceholder')}</option>
                            {exactSkillOptions.map((v) => (
                              <option key={v} value={v}>
                                {v}
                              </option>
                            ))}
                          </Select>
                        </>
                      ) : null}
                      {(loadingCountries || loadingCantons) && (
                        <Hint style={{ color: '#0369a1' }}>{t('playerRegister.loadingRegionsCantons')}</Hint>
                      )}
                      <BtnRow>
                        <BtnOutline type="button" onClick={goBack}>
                          {t('playerRegister.back')}
                        </BtnOutline>
                        <BtnPrimary type="button" disabled={step34Blocked} onClick={goNext}>
                          {t('playerRegister.continue')}
                        </BtnPrimary>
                      </BtnRow>
                    </motion.div>
                  )}

                  {wizardStep === 4 && (
                    <motion.div key="w4" {...slideSx}>
                      <StepTitle>{t('playerRegister.step4Title')}</StepTitle>
                      <StepLead>{t('playerRegister.step4Lead')}</StepLead>
                      <SummaryBlock>
                        <SummaryHeadRow>
                          <SummaryHeading>{t('playerRegister.summaryAccount')}</SummaryHeading>
                          <SummaryEdit type="button" onClick={() => setWizardStep(1)}>
                            {t('playerRegister.edit')}
                          </SummaryEdit>
                        </SummaryHeadRow>
                        <SummaryBody>{form.email?.trim() || '—'}</SummaryBody>
                      </SummaryBlock>
                      <SummaryBlock>
                        <SummaryHeadRow>
                          <SummaryHeading>{t('playerRegister.summaryPersonal')}</SummaryHeading>
                          <SummaryEdit type="button" onClick={() => setWizardStep(2)}>
                            {t('playerRegister.edit')}
                          </SummaryEdit>
                        </SummaryHeadRow>
                        <SummaryBody>
                          {[form.firstName, form.lastName].filter(Boolean).join(' ') || '—'}
                          <br />
                          {form.phoneNumber?.trim() || t('playerRegister.summaryNoPhone')}
                        </SummaryBody>
                      </SummaryBlock>
                      <SummaryBlock>
                        <SummaryHeadRow>
                          <SummaryHeading>{t('playerRegister.summaryClubSkill')}</SummaryHeading>
                          <SummaryEdit type="button" onClick={() => setWizardStep(3)}>
                            {t('playerRegister.edit')}
                          </SummaryEdit>
                        </SummaryHeadRow>
                        <SummaryBody>
                          {t('playerRegister.summaryCanton')}: {cantonLabel || '—'}
                          <br />
                          {t('playerRegister.summarySkill')}: {skillSummary}
                          <br />
                          <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>
                            {t('playerRegister.profileCompleteAfterLogin')}
                          </span>
                        </SummaryBody>
                      </SummaryBlock>
                      <PlayerRegistrationConsentSection
                        agreeTerms={agreeTerms}
                        onAgreeTermsChange={setAgreeTerms}
                        agreeRules={agreeRules}
                        onAgreeRulesChange={setAgreeRules}
                        partnerCommunications={partnerCommunications}
                        onPartnerCommunicationsChange={setPartnerCommunications}
                      />
                      <PrivacyNote>{t('playerRegister.privacyNote')}</PrivacyNote>
                      {(loadingCountries || loadingCantons) && (
                        <Hint style={{ color: '#0369a1' }}>{t('playerRegister.loadingRegionsCantons')}</Hint>
                      )}
                      <BtnRow>
                        <BtnOutline type="button" disabled={submitting} onClick={goBack}>
                          {t('playerRegister.back')}
                        </BtnOutline>
                        <BtnPrimary type="button" disabled={step34Blocked || submitting} onClick={runRegisterStart}>
                          {submitting ? (
                            t('playerRegister.sending')
                          ) : (
                            <>
                              <FaEnvelope aria-hidden /> {t('playerRegister.sendVerification')}
                            </>
                          )}
                        </BtnPrimary>
                      </BtnRow>
                    </motion.div>
                  )}
                </AnimatePresence>
              </WizardPanel>
            </>
          )}

          <LoginLink>
            {t('playerRegister.alreadyHaveAccount')} <Link to="/login">{t('playerRegister.logIn')}</Link>
          </LoginLink>
        </Card>
      </PageWrap>
      <SkillReferenceModal open={skillMappingModalOpen} onClose={() => setSkillMappingModalOpen(false)} />
    </>
  );
}
